import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HiWifi, HiX, HiCheckCircle, HiExclamationCircle, HiInformationCircle } from 'react-icons/hi';
import Button from './Button';
import { API_BASE_URL, config } from '../../config';
import { api } from '../../utils/api';

interface ConnectionTestResult {
  success: boolean;
  status?: number;
  message: string;
  responseTime?: number;
  timestamp: Date;
  details?: any;
}

const ConnectionTest: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [result, setResult] = useState<ConnectionTestResult | null>(null);

  const testConnection = async () => {
    setIsTesting(true);
    const startTime = Date.now();
    
    try {
      // Test the health endpoint
      const response = await api.get('/api/health');
      const responseTime = Date.now() - startTime;
      
      setResult({
        success: true,
        status: response.status,
        message: 'Connection successful!',
        responseTime,
        timestamp: new Date(),
        details: response.data,
      });
    } catch (error: any) {
      const responseTime = Date.now() - startTime;
      
      setResult({
        success: false,
        status: error.response?.status,
        message: error.message || 'Connection failed',
        responseTime,
        timestamp: new Date(),
        details: {
          error: error.message,
          code: error.code,
          response: error.response?.data,
        },
      });
    } finally {
      setIsTesting(false);
    }
  };

  const toggleModal = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setResult(null); // Reset result when opening
    }
  };

  return (
    <>
      {/* Trigger Button */}
      <Button
        variant="outline"
        size="sm"
        onClick={toggleModal}
        icon={<HiWifi />}
      >
        <span className="hidden sm:inline">Connection</span>
      </Button>

      {/* Modal */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
              onClick={toggleModal}
            />

            {/* Modal Content */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
              onClick={toggleModal}
            >
              <div
                className="bg-white dark:bg-dark-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-200 dark:border-dark-700"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-dark-700">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
                      <HiWifi className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                        Backend Connection Test
                      </h2>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Verify API connectivity in production
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={toggleModal}
                    className="p-2 hover:bg-gray-100 dark:hover:bg-dark-700 rounded-lg transition-colors"
                  >
                    <HiX className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                  </button>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6">
                  {/* Environment Info */}
                  <div className="space-y-3">
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider flex items-center gap-2">
                      <HiInformationCircle className="w-4 h-4" />
                      Configuration
                    </h3>
                    <div className="bg-gray-50 dark:bg-dark-900 rounded-lg p-4 space-y-2 font-mono text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Environment:</span>
                        <span className="font-semibold text-gray-900 dark:text-white">
                          {config.isProduction ? 'Production' : config.isDevelopment ? 'Development' : 'Unknown'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">API Base URL:</span>
                        <span className="font-semibold text-gray-900 dark:text-white break-all">
                          {API_BASE_URL}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">App Version:</span>
                        <span className="font-semibold text-gray-900 dark:text-white">
                          {config.appVersion}
                        </span>
                      </div>
                    </div>
                    
                    {/* Warning if using localhost in production */}
                    {config.isProduction && API_BASE_URL.includes('localhost') && (
                      <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                        <div className="flex gap-3">
                          <HiExclamationCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-500 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-sm font-medium text-yellow-800 dark:text-yellow-300">
                              Warning: Using localhost in production
                            </p>
                            <p className="text-xs text-yellow-700 dark:text-yellow-400 mt-1">
                              Please configure REACT_APP_API_BASE_URL in your .env.production file
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Test Button */}
                  <div>
                    <Button
                      variant="primary"
                      onClick={testConnection}
                      disabled={isTesting}
                      className="w-full"
                      icon={<HiWifi />}
                    >
                      {isTesting ? 'Testing Connection...' : 'Test Connection'}
                    </Button>
                  </div>

                  {/* Test Results */}
                  {result && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="space-y-3"
                    >
                      <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                        Test Results
                      </h3>

                      {/* Status Card */}
                      <div
                        className={`rounded-lg p-4 border ${
                          result.success
                            ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                            : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          {result.success ? (
                            <HiCheckCircle className="w-6 h-6 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                          ) : (
                            <HiExclamationCircle className="w-6 h-6 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                          )}
                          <div className="flex-1">
                            <p
                              className={`font-semibold ${
                                result.success
                                  ? 'text-green-900 dark:text-green-300'
                                  : 'text-red-900 dark:text-red-300'
                              }`}
                            >
                              {result.message}
                            </p>
                            <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                              {result.timestamp.toLocaleString()}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Details */}
                      <div className="bg-gray-50 dark:bg-dark-900 rounded-lg p-4 space-y-2 font-mono text-sm">
                        {result.status && (
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-400">HTTP Status:</span>
                            <span
                              className={`font-semibold ${
                                result.success
                                  ? 'text-green-600 dark:text-green-400'
                                  : 'text-red-600 dark:text-red-400'
                              }`}
                            >
                              {result.status}
                            </span>
                          </div>
                        )}
                        {result.responseTime !== undefined && (
                          <div className="flex justify-between">
                            <span className="text-gray-600 dark:text-gray-400">Response Time:</span>
                            <span className="font-semibold text-gray-900 dark:text-white">
                              {result.responseTime}ms
                            </span>
                          </div>
                        )}
                        <div className="flex justify-between">
                          <span className="text-gray-600 dark:text-gray-400">Endpoint Tested:</span>
                          <span className="font-semibold text-gray-900 dark:text-white">
                            /api/health
                          </span>
                        </div>
                      </div>

                      {/* Raw Response Data */}
                      {result.details && (
                        <details className="bg-gray-50 dark:bg-dark-900 rounded-lg">
                          <summary className="cursor-pointer p-4 font-medium text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-dark-800 rounded-lg transition-colors">
                            View Raw Response
                          </summary>
                          <div className="p-4 pt-0">
                            <pre className="text-xs text-gray-700 dark:text-gray-300 overflow-x-auto">
                              {JSON.stringify(result.details, null, 2)}
                            </pre>
                          </div>
                        </details>
                      )}

                      {/* Troubleshooting Tips */}
                      {!result.success && (
                        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                          <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-300 mb-2">
                            Troubleshooting Tips:
                          </h4>
                          <ul className="text-xs text-blue-800 dark:text-blue-400 space-y-1 list-disc list-inside">
                            <li>Verify the backend server is running</li>
                            <li>Check REACT_APP_API_BASE_URL in .env.production</li>
                            <li>Ensure CORS is properly configured on the backend</li>
                            <li>Verify network connectivity and firewall settings</li>
                            <li>Check browser console for additional error details</li>
                          </ul>
                        </div>
                      )}
                    </motion.div>
                  )}
                </div>

                {/* Footer */}
                <div className="p-6 border-t border-gray-200 dark:border-dark-700 bg-gray-50 dark:bg-dark-900">
                  <p className="text-xs text-gray-600 dark:text-gray-400 text-center">
                    This tool helps verify that your frontend can communicate with the backend API.
                    Use it to diagnose connection issues in production environments.
                  </p>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default ConnectionTest;

